/**
 * VirtualSpace 12-col grid — Space-row planner (migration 353).
 *
 * Walks an ordered list of blocks and groups consecutive blocks
 * into Space rows. A Space row is one row of the 12-column CSS
 * Grid that the public renderer paints; the planner is the single
 * source of truth for "given these N blocks, how many Spaces do
 * we render and which block lives in each?".
 *
 * Pure helper — no DOM, no React, no server. Consumed by:
 *   - The public renderer (`virtual-space-public-renderer.tsx`)
 *     which iterates the planner output to emit Space `<section>`
 *     wrappers.
 *   - The rail builder (`public-virtual-space-rail.ts`) which
 *     navigates by Space row, not by individual block.
 *   - The editor's lane indicator (which shows "8/12 used in this
 *     row") so the operator sees the same row partitioning the
 *     renderer will use.
 *
 * Rules:
 *   1. Blocks are walked in input order (caller is responsible
 *      for `sort_order` ascending — the planner trusts the
 *      sequence).
 *   2. A new Space row opens implicitly when the running col-sum
 *      hits or exceeds 12. The block that would have overflowed
 *      lands at the start of the next row (the previous row keeps
 *      whatever fit).
 *   3. A new Space row also opens when the next block carries
 *      `force_new_row=true` — even when the previous row has open
 *      columns. The operator's escape hatch from the auto-flow.
 *   4. `force_new_row` on the FIRST block is a no-op (there's no
 *      previous row to break out of).
 *   5. Empty input yields an empty array. No empty Space rows are
 *      ever emitted.
 *   6. `col_span` is clamped to 1..12 defensively. The DB CHECK
 *      enforces the same range, but a bad fixture or a partial
 *      row that bypassed the action's clamp shouldn't crash the
 *      planner.
 *
 * The planner does NOT decide:
 *   - Whether a Space row qualifies for theater mode — see
 *     `isSpaceRowTheaterEligible` for that predicate (it consumes
 *     planner output but lives separately so the planner stays
 *     independent of the theater rules, which can churn).
 *   - Mobile-collapse behaviour — every block becomes full-width
 *     below the `sm` breakpoint via Tailwind responsive classes
 *     in the renderer. The planner's output shape is unchanged
 *     across breakpoints.
 */

import {
  PUBLIC_VIRTUAL_SPACE_THEATER_BLOCK_TYPES,
} from "@/lib/virtualspace/public-virtual-space-theater";
import type { VirtualSpaceBlockRow } from "@/lib/virtualspace/virtual-space-types";

/** One Space row — a horizontal partition of the 12-column grid. */
export interface SpaceRow<B extends VirtualSpaceBlockRow = VirtualSpaceBlockRow> {
  /** 0-based index into the planner's output array. */
  spaceIndex: number;
  /** Blocks living in this row, in their input order. */
  blocks: B[];
  /** Sum of `col_span` across the blocks in this row (1..12). */
  filledColumns: number;
  /**
   * Convenience pointer to the first block. Anchors the rail entry
   * + the renderer's `data-vs-rail-block-id` so navigating by Space
   * still works after the per-block-rail-anchor model is gone.
   */
  anchorBlock: B;
}

/** Defensive clamp — match the DB CHECK constraint on col_span. */
function clampColSpan(raw: number): number {
  if (!Number.isFinite(raw)) return 12;
  if (raw < 1) return 1;
  if (raw > 12) return 12;
  return Math.floor(raw);
}

/**
 * Plan the Space rows for an ordered list of blocks. See module
 * doc above for the rules; see the unit tests for the canonical
 * row partitions.
 */
export function planSpaceRows<B extends VirtualSpaceBlockRow>(
  blocks: readonly B[],
): SpaceRow<B>[] {
  if (blocks.length === 0) return [];

  const rows: SpaceRow<B>[] = [];
  let current: B[] = [];
  let currentSum = 0;

  const flush = () => {
    if (current.length === 0) return;
    rows.push({
      spaceIndex: rows.length,
      blocks: current,
      filledColumns: currentSum,
      anchorBlock: current[0],
    });
    current = [];
    currentSum = 0;
  };

  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i];
    const colSpan = clampColSpan(block.col_span);
    // `force_new_row` on the very first block of the input has no
    // effect — there's no previous row to break out of. Skipping
    // the flush keeps the output free of empty Space rows.
    const wantsNewRow = block.force_new_row && current.length > 0;
    const wouldOverflow = currentSum + colSpan > 12;
    if (wantsNewRow || wouldOverflow) flush();
    current.push(block);
    currentSum += colSpan;
    // Edge case: a clamped 12-column block fills the row exactly;
    // close it eagerly so the next block starts a fresh row even
    // without `force_new_row`. This keeps the partition stable
    // when blocks happen to fill exactly 12.
    if (currentSum >= 12) flush();
  }

  // Trailing partial row.
  flush();

  return rows;
}

/**
 * Predicate: is this Space row eligible for theater mode? Theater
 * is a "this one thing is the show" treatment — it requires:
 *
 *   1. Exactly one block in the row (split rows never theater —
 *      8 + 4 is "two things side-by-side", not "the show").
 *   2. That block's type is in the existing
 *      `PUBLIC_VIRTUAL_SPACE_THEATER_BLOCK_TYPES` set (media
 *      surfaces only).
 *
 * Lives next to the planner so callers (renderer + tests) can
 * import a single helper rather than re-implementing the rule.
 */
export function isSpaceRowTheaterEligible(
  row: SpaceRow,
): boolean {
  if (row.blocks.length !== 1) return false;
  const only = row.blocks[0];
  return PUBLIC_VIRTUAL_SPACE_THEATER_BLOCK_TYPES.has(only.block_type);
}
